import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { 
  Shield, Heart, Plane, Users, Home, HeartPulse, Accessibility,
  ExternalLink, ArrowRight, Building, Umbrella, Baby, Car
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import QuoteEstimator from '../components/QuoteEstimator';
import { useLanguage } from '../context/LanguageContext';

// Insurance carrier data with brand colors
const insuranceCarriers = [
  { name: "iA Financial Group", bg: '#003366', text: '#ffffff' },
  { name: "Manulife", bg: '#00a758', text: '#ffffff' },
  { name: "Sun Life", bg: '#ffd100', text: '#000000' },
  { name: "Canada Life", bg: '#00205b', text: '#ffffff' },
  { name: "Empire Life", bg: '#005baa', text: '#ffffff' },
  { name: "RBC Insurance", bg: '#0051a5', text: '#ffffff' },
  { name: "Equitable Life", bg: '#003087', text: '#ffffff' },
  { name: "Assumption Life", bg: '#1a365d', text: '#ffffff' },
  { name: "Humania", bg: '#e31837', text: '#ffffff' },
  { name: "Beneva", bg: '#00205b', text: '#ffffff' },
  { name: "Canada Protection Plan", bg: '#c41230', text: '#ffffff' },
  { name: "GMS Insurance", bg: '#0072bc', text: '#ffffff' },
  { name: "Destination Travel", bg: '#003366', text: '#ffffff' },
  { name: "Travelance", bg: '#00457c', text: '#ffffff' },
  { name: "RIMI Insurance", bg: '#1e4d8c', text: '#ffffff' }
];

// Translations for Insurance page
const pageText = {
  en: {
    hero: {
      title: 'Insurance Solutions',
      subtitle: "Comprehensive protection for you and your loved ones through Canada's leading insurance carriers",
      cta: 'Get a Free Quote'
    },
    lifeHealth: {
      title: 'Life & Health Protection',
      subtitle: "Essential coverage to protect you and your family's financial security",
      life: { title: 'Life Insurance', desc: 'Protect your loved ones with comprehensive life insurance coverage. Term, whole life, and universal life options available.' },
      critical: { title: 'Critical Illness Insurance', desc: 'Lump-sum payment upon diagnosis of covered conditions like cancer, heart attack, or stroke.' },
      disability: { title: 'Disability Insurance', desc: "Income replacement if you're unable to work due to illness or injury. Short-term and long-term options." },
      longTerm: { title: 'Long-Term Care Insurance', desc: 'Coverage for extended care services when you need assistance with daily living activities.' }
    },
    travel: {
      title: 'Travel & Visitor Insurance',
      subtitle: 'Protection for travelers and visitors to Canada',
      travel: { title: 'Travel Insurance', desc: 'Comprehensive coverage for medical emergencies, trip cancellation, and lost baggage when traveling abroad.' },
      visitors: { title: 'Visitors to Canada', desc: 'Medical insurance for tourists, parents, grandparents, and visitors to Canada. Includes pre-existing conditions coverage options.' },
      superVisa: { title: 'Super Visa Insurance', desc: 'Minimum $100,000 coverage required for Super Visa applications. Valid for 1 year from a Canadian insurance company.' }
    },
    family: {
      title: 'Family & Personal Insurance',
      subtitle: 'Protect every member of your family',
      child: { title: 'Child Insurance Plans', desc: 'Early coverage for your children with locked-in rates and cash value accumulation.' },
      mortgage: { title: 'Mortgage Insurance', desc: 'Protect your family from mortgage payments if something happens to you. Portable coverage that stays with you.' },
      home: { title: 'Home & Auto Insurance', desc: 'Bundle your home and auto insurance for comprehensive protection and savings. Available through our partner Westland Insurance Northgate.' }
    },
    business: {
      title: 'Business & Group Insurance',
      subtitle: 'Solutions for business owners and organizations',
      group: { title: 'Group Benefits', desc: 'Comprehensive health and dental plans for your employees. Attract and retain top talent.' },
      keyPerson: { title: 'Key Person Insurance', desc: 'Protect your business from the financial impact of losing a key employee or partner.' },
      succession: { title: 'Business Succession', desc: 'Fund buy-sell agreements and ensure smooth business transitions with life insurance.' }
    },
    carriers: {
      title: 'Our Insurance Partners',
      subtitle: "We work with Canada's top insurance providers to find you the best coverage"
    },
    quote: {
      title: 'Insurance Quote Estimator',
      subtitle: 'Get an instant estimate for your insurance coverage'
    },
    cta: {
      title: 'Ready to Protect What Matters Most?',
      subtitle: 'Our licensed insurance advisors are here to help you find the right coverage at the best rates.',
      button1: 'Contact Us Today',
      button2: 'Meet Our Team'
    }
  },
  fr: {
    hero: {
      title: "Solutions d'Assurance",
      subtitle: "Protection complète pour vous et vos proches grâce aux principaux assureurs du Canada",
      cta: 'Obtenir une Soumission Gratuite'
    },
    lifeHealth: {
      title: 'Protection Vie & Santé',
      subtitle: "Couverture essentielle pour protéger la sécurité financière de votre famille",
      life: { title: 'Assurance Vie', desc: "Protégez vos proches avec une couverture d'assurance vie complète. Options temporaire, vie entière et universelle disponibles." },
      critical: { title: 'Assurance Maladies Graves', desc: "Paiement forfaitaire lors du diagnostic de conditions couvertes comme le cancer, crise cardiaque ou AVC." },
      disability: { title: 'Assurance Invalidité', desc: "Remplacement de revenu si vous êtes incapable de travailler en raison d'une maladie ou d'une blessure." },
      longTerm: { title: 'Assurance Soins de Longue Durée', desc: "Couverture pour les services de soins prolongés lorsque vous avez besoin d'aide pour les activités quotidiennes." }
    },
    travel: {
      title: 'Assurance Voyage & Visiteurs',
      subtitle: 'Protection pour les voyageurs et visiteurs au Canada',
      travel: { title: 'Assurance Voyage', desc: "Couverture complète pour les urgences médicales, l'annulation de voyage et les bagages perdus." },
      visitors: { title: 'Visiteurs au Canada', desc: "Assurance médicale pour les touristes, parents, grands-parents et visiteurs au Canada. Options de couverture pour conditions préexistantes." },
      superVisa: { title: 'Assurance Super Visa', desc: "Couverture minimale de 100 000$ requise pour les demandes de Super Visa. Valide 1 an auprès d'une compagnie canadienne." }
    },
    family: {
      title: 'Assurance Famille & Personnelle',
      subtitle: 'Protégez chaque membre de votre famille',
      child: { title: 'Régimes pour Enfants', desc: "Couverture précoce pour vos enfants avec taux verrouillés et accumulation de valeur de rachat." },
      mortgage: { title: 'Assurance Hypothécaire', desc: "Protégez votre famille des paiements hypothécaires si quelque chose vous arrive. Couverture transférable." },
      home: { title: 'Assurance Habitation & Auto', desc: "Regroupez vos assurances habitation et auto pour une protection complète et des économies. Disponible via notre partenaire Westland Insurance Northgate." }
    },
    business: {
      title: 'Assurance Entreprise & Groupe',
      subtitle: 'Solutions pour propriétaires d\'entreprise et organisations',
      group: { title: 'Avantages Collectifs', desc: "Régimes complets de santé et dentaires pour vos employés. Attirez et retenez les meilleurs talents." },
      keyPerson: { title: 'Assurance Personne Clé', desc: "Protégez votre entreprise de l'impact financier de la perte d'un employé ou partenaire clé." },
      succession: { title: 'Succession d\'Entreprise', desc: "Financez les conventions d'achat-vente et assurez des transitions d'entreprise en douceur." }
    },
    carriers: {
      title: 'Nos Partenaires Assureurs',
      subtitle: "Nous travaillons avec les meilleurs assureurs du Canada pour vous trouver la meilleure couverture"
    },
    quote: {
      title: "Estimateur de Soumission d'Assurance",
      subtitle: "Obtenez une estimation instantanée pour votre couverture d'assurance"
    },
    cta: {
      title: 'Prêt à Protéger Ce Qui Compte le Plus?',
      subtitle: "Nos conseillers en assurance licenciés sont là pour vous aider à trouver la bonne couverture aux meilleurs taux.",
      button1: "Contactez-Nous Aujourd'hui",
      button2: 'Rencontrer Notre Équipe'
    }
  }
};

const InsurancePage = () => {
  const { language } = useLanguage();
  const text = pageText[language];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden bg-[#0077B6]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0077B6] to-[#005A8C]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">{text.hero.title}</h1>
          <p className="text-lg text-blue-100 mb-6">
            {text.hero.subtitle}
          </p>
          <Button asChild size="lg" className="bg-[#D4AF37] text-[#003D5C] hover:bg-[#B8962E] font-bold">
            <a href="#quote">{text.hero.cta}</a>
          </Button>
        </div>
      </section>

      {/* ========== SECTION 1: Life & Health Protection (White Background) ========== */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">{text.lifeHealth.title}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{text.lifeHealth.subtitle}</p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <Shield className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.lifeHealth.life.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.lifeHealth.life.desc}</p>
                <div className="space-y-1">
                  <a href="https://www.empire.ca/insurance/term-life-insurance" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />Empire Life Term
                  </a>
                  <a href="https://ia.ca/individuals/insurance/life-insurance" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />iA Financial Life
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <Heart className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.lifeHealth.critical.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.lifeHealth.critical.desc}</p>
                <div className="space-y-1">
                  <a href="https://ia.ca/individuals/insurance/health-disability-insurance/critical-illness-insurance" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />iA Financial Critical Illness
                  </a>
                  <a href="https://www.empire.ca/advisor/insurance/critical-illness-insurance" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />Empire Life CI
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <Accessibility className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.lifeHealth.disability.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.lifeHealth.disability.desc}</p>
                <div className="space-y-1">
                  <a href="https://ia.ca/individuals/insurance/health-disability-insurance/disability-income-insurance" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />iA Financial Disability
                  </a>
                  <a href="https://www.equitable.ca/products/group-benefits/disability-management-solutions" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />Equitable Life Disability
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <HeartPulse className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.lifeHealth.longTerm.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.lifeHealth.longTerm.desc}</p>
                <div className="space-y-1">
                  <a href="https://ia.ca/health-disability-insurance" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />iA Financial Health
                  </a>
                  <a href="https://www.empire.ca/group-benefits/critical-illness-and-disability" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />Empire Life Benefits
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== SECTION 2: Travel & Visitor Insurance (Dark Background) ========== */}
      <section className="py-12 bg-[#003D5C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-3">{text.travel.title}</h2>
            <p className="text-blue-200 max-w-2xl mx-auto">{text.travel.subtitle}</p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <Plane className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{text.travel.travel.title}</h3>
                <p className="text-blue-200 text-sm mb-3">{text.travel.travel.desc}</p>
                <div className="space-y-1">
                  <a href="https://gms.ca/travel-insurance" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />GMS Travel Insurance
                  </a>
                  <a href="https://www.bluecross.ca" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />Blue Cross Canada
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <Plane className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{text.travel.visitors.title}</h3>
                <p className="text-blue-200 text-sm mb-3">{text.travel.visitors.desc}</p>
                <div className="space-y-1">
                  <a href="https://gms.ca/travel-insurance" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />GMS Visitors to Canada
                  </a>
                  <a href="https://www.medipac.com" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />Medipac Travel Insurance
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <Plane className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{text.travel.superVisa.title}</h3>
                <p className="text-blue-200 text-sm mb-3">{text.travel.superVisa.desc}</p>
                <div className="space-y-1">
                  <a href="https://www.bluecross.ca" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />Blue Cross Super Visa
                  </a>
                  <a href="https://gms.ca/travel-insurance" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />GMS Super Visa Insurance
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== SECTION 3: Property & Asset Protection (White Background) ========== */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">Property & Asset Protection</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Protect your home, mortgage, and valuable assets</p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <Home className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">Mortgage Protection Insurance</h3>
                <p className="text-gray-600 text-sm mb-3">Ensure your home is protected and mortgage paid if the unexpected happens to you or your family.</p>
                <div className="space-y-1">
                  <a href="https://www.empire.ca/life-insurance/permanent-life-insurance" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />Empire Life
                  </a>
                  <a href="https://ia.ca/individuals/insurance/life-insurance" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />iA Financial
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <Building className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">Home Insurance</h3>
                <p className="text-gray-600 text-sm mb-3">Protect your home and belongings from fire, theft, and natural disasters.</p>
                <p className="text-xs text-[#0077B6]">Partner: Westland Insurance Northgate</p>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <Umbrella className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">Creditor Insurance</h3>
                <p className="text-gray-600 text-sm mb-3">Protection for loans and credit lines to ensure debts are covered in case of death or disability.</p>
                <div className="space-y-1">
                  <a href="https://ia.ca/individuals/insurance/life-insurance" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />iA Financial
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== SECTION 4: Business & Group Insurance (Dark Background) ========== */}
      <section className="py-12 bg-[#003D5C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-3">Business & Group Insurance</h2>
            <p className="text-blue-200 max-w-2xl mx-auto">Employee benefits and business protection solutions</p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">Group Insurance</h3>
                <p className="text-blue-200 text-sm mb-3">Comprehensive employee benefit plans for businesses of all sizes. Health, dental, vision, and life coverage.</p>
                <div className="space-y-1">
                  <a href="https://ia.ca/group-insurance" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />iA Financial Group Benefits
                  </a>
                  <a href="https://www.equitable.ca/products/group-benefits" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />Equitable Life Group
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <Building className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">Key Person Insurance</h3>
                <p className="text-blue-200 text-sm mb-3">Protect your business from financial loss if a key employee or owner becomes disabled or passes away.</p>
                <div className="space-y-1">
                  <a href="https://www.empire.ca/advisor/insurance/permanent-non-participating-whole-life-insurance" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />Empire Life Business
                  </a>
                  <a href="https://ia.ca/individuals/insurance/life-insurance" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />iA Financial Life
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== Quote Estimator Section ========== */}
      <section className="py-12 bg-[#F0F7FF]" id="quote">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <QuoteEstimator />
        </div>
      </section>

      {/* ========== CTA Section ========== */}
      <section className="py-12 bg-[#0077B6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Need Help Choosing the Right Insurance?</h2>
          <p className="text-blue-100 mb-6 text-lg">
            Our expert advisors are here to help you find the perfect coverage for your needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#003D5C] font-bold">
              <Link to="/team">Speak with an Advisor</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#0077B6] font-bold">
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ========== Insurance Carriers Grid (Static Logos) ========== */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Our Insurance Carrier Partners</h3>
            <p className="text-gray-600">We work with Canada's leading insurance companies to find you the best coverage</p>
          </div>
          
          <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
            {insuranceCarriers.map((carrier) => (
              <div
                key={carrier.name}
                className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 flex items-center justify-center h-16 hover:shadow-md transition-shadow"
              >
                <div 
                  className="px-3 py-1.5 rounded text-center w-full"
                  style={{ backgroundColor: carrier.bg }}
                >
                  <span 
                    className="font-bold text-xs"
                    style={{ color: carrier.text }}
                  >
                    {carrier.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsurancePage;
